import { Injectable } from '@angular/core';

import {
  LEASE_STALE_MS,
  LeaderInfo,
  SESSION_KEY,
  SessionInfo,
  TABS_KEY,
  TabLease,
  TabRegistry,
} from './session.models';

@Injectable({ providedIn: 'root' })
export class SessionStorageService {
  readSession(): SessionInfo | null {
    return this.read<SessionInfo>(SESSION_KEY);
  }

  writeSession(session: SessionInfo): void {
    this.write(SESSION_KEY, session);
  }

  clearSession(): void {
    this.remove(SESSION_KEY);
  }

  readRegistry(now: number = Date.now()): TabRegistry {
    return this.prune(this.read<TabRegistry>(TABS_KEY) ?? {}, now);
  }

  clearRegistry(): void {
    this.remove(TABS_KEY);
  }

  refreshLease(lease: TabLease, now: number = Date.now()): TabRegistry {
    const registry = this.readRegistry(now);
    registry[lease.tabId] = lease;
    this.write(TABS_KEY, registry);
    return registry;
  }

  removeLease(tabId: string, now: number = Date.now()): TabRegistry {
    const registry = this.readRegistry(now);
    delete registry[tabId];
    this.write(TABS_KEY, registry);
    return registry;
  }

  resolveLeader(registry: TabRegistry, selfTabId: string): LeaderInfo {
    const leases = Object.values(registry);
    if (leases.length === 0) {
      return { tabId: null, isSelf: false };
    }

    const leader = leases.reduce((a, b) => {
      if (a.joinedAt !== b.joinedAt) {
        return a.joinedAt < b.joinedAt ? a : b;
      }
      return a.tabId < b.tabId ? a : b;
    });

    return { tabId: leader.tabId, isSelf: leader.tabId === selfTabId };
  }

  latestActivityAt(registry: TabRegistry): number {
    return Object.values(registry).reduce(
      (latest, lease) => Math.max(latest, lease.lastActivityAt),
      0
    );
  }

  private prune(registry: TabRegistry, now: number): TabRegistry {
    const live: TabRegistry = {};
    for (const lease of Object.values(registry)) {
      if (now - lease.lastSeenAt < LEASE_STALE_MS) {
        live[lease.tabId] = lease;
      }
    }
    return live;
  }

  private read<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? null : (JSON.parse(raw) as T);
    } catch {
      this.remove(key);
      return null;
    }
  }

  private write(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Quota exceeded or storage disabled. Auditing must never break the app.
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage disabled. Nothing to do.
    }
  }
}
